'use client';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSupabase } from '~/lib/supabase/browser';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '~/components/ui/form';
import { cn } from 'cn';
import { Input } from '~/components/ui/input';
import { Button } from '~/components/ui/button';
import { Spinner } from '~/components/ui/spinner';
/** Matches `otp_length` in supabase/config.toml; a complete code verifies without a click. */
const CODE_LENGTH = 6;
/** Client-side pause before another code; Supabase Auth still enforces its own limit. */
const RESEND_SECONDS = 30;
const schema = z.object({
  email: z.email(),
  code: z
    .string()
    .regex(/^\d{6,10}$/)
    .optional(),
});
export function EmailOtp({
  defaultEmail = '',
  onVerified,
  confirmation = false,
  autoSend = false,
  invitationToken,
  authPage,
}: {
  defaultEmail?: string;
  onVerified: () => void;
  confirmation?: boolean;
  /** Send the code immediately for an email the person has already entered. */
  autoSend?: boolean;
  invitationToken?: string;
  authPage?: 'signin' | 'signup';
}) {
  const client = useSupabase();
  const [state, setState] = useState({ sent: false, busy: false, error: '', resendIn: 0 });
  const busy = useRef(false);
  const verifiedCode = useRef<string | undefined>(undefined);
  const autoSent = useRef(false);
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: defaultEmail },
    // A partial code is not an error: validate on submit, not on every keystroke after the email went out.
    reValidateMode: 'onSubmit',
  });
  useEffect(() => {
    if (state.resendIn <= 0) return;
    const timer = setTimeout(() => setState((s) => ({ ...s, resendIn: s.resendIn - 1 })), 1000);
    return () => clearTimeout(timer);
  }, [state.resendIn]);
  async function send(email: string) {
    const hash =
      invitationToken && !confirmation
        ? Array.from(
            new Uint8Array(
              await crypto.subtle.digest('SHA-256', new TextEncoder().encode(invitationToken)),
            ),
            (byte) => byte.toString(16).padStart(2, '0'),
          ).join('')
        : undefined;
    const { error } = await client.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: !confirmation,
        ...(hash ? { data: { workspace_invitation_hash: hash } } : {}),
      },
    });
    if (error) throw error;
  }
  async function submit(i: z.infer<typeof schema>) {
    // The code field submits itself when complete; never verify one code twice.
    if (busy.current || (state.sent && i.code === verifiedCode.current)) return;
    busy.current = true;
    setState((s) => ({ ...s, busy: true, error: '' }));
    try {
      if (!state.sent) {
        await send(i.email);
        setState((s) => ({ ...s, sent: true, busy: false, resendIn: RESEND_SECONDS }));
      } else {
        const { error } = await client.auth.verifyOtp({
          email: i.email,
          token: i.code ?? '',
          type: 'email',
        });
        if (error) throw error;
        // A used code is never submitted again, but the form stays usable in case
        // the caller keeps it on screen (for example when a follow-up step fails).
        verifiedCode.current = i.code;
        setState((s) => ({ ...s, busy: false }));
        onVerified();
      }
    } catch {
      setState((s) => ({
        ...s,
        busy: false,
        error: state.sent
          ? 'That code is invalid or expired. Please try again.'
          : 'We could not send your code. Please try again shortly.',
      }));
    } finally {
      busy.current = false;
    }
  }
  async function resend() {
    if (busy.current) return;
    busy.current = true;
    setState((s) => ({ ...s, busy: true, error: '' }));
    const before = form.getValues('code');
    try {
      await send(form.getValues('email'));
      // Clear the previous code unless a new one was typed while sending.
      if (form.getValues('code') === before) form.setValue('code', undefined);
      setState((s) => ({ ...s, busy: false, resendIn: RESEND_SECONDS }));
    } catch {
      setState((s) => ({
        ...s,
        busy: false,
        error: 'We could not send a new code. Please try again shortly.',
      }));
    } finally {
      busy.current = false;
    }
    // A complete code typed while the new one was being requested still verifies.
    const typed = form.getValues('code');
    if (typed !== before && typed?.length === CODE_LENGTH) void form.handleSubmit(submit)();
  }
  // Refs survive Strict Mode's remount, so a remembered email gets exactly one code.
  useEffect(() => {
    if (!autoSend || !defaultEmail || autoSent.current) return;
    autoSent.current = true;
    void form.handleSubmit(submit)();
    // Runs once, for the email the form opened with.
  }, []);
  return (
    <Form {...form}>
      <form className="space-y-5" onSubmit={form.handleSubmit(submit)} data-test="email-otp-form">
        {authPage && (
          <div>
            <h1 className="text-[32px] font-semibold tracking-tight">
              {state.sent
                ? 'Check your email'
                : authPage === 'signup'
                  ? 'Add Ava to your team.'
                  : 'Sign in'}
            </h1>
            <p className="mt-3 text-muted-foreground">
              {state.sent
                ? `We sent a 6-digit code to ${form.getValues('email')}.`
                : authPage === 'signup'
                  ? 'Start with your email. We’ll create your workspace, then you can add your first property.'
                  : 'We’ll email you a 6-digit code. No password needed.'}
            </p>
          </div>
        )}
        {!(authPage && state.sent) && (
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Work email</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    required
                    placeholder="you@company.com"
                    className="h-12"
                    autoComplete="email"
                    disabled={state.sent || confirmation}
                    data-test="auth-email-input"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {state.sent && (
          <>
            {!authPage && (
              <p className="text-sm text-muted-foreground" role="status">
                Enter the {CODE_LENGTH}-digit code sent to {form.getValues('email')}.
              </p>
            )}
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Verification code</FormLabel>
                  <div className="group/code relative">
                    <div aria-hidden="true" className="grid grid-cols-6 gap-2">
                      {Array.from({ length: CODE_LENGTH }, (_, index) => {
                        // The box the next digit lands in; the last box once the code is full.
                        const active =
                          index === Math.min(field.value?.length ?? 0, CODE_LENGTH - 1);
                        return (
                          <span
                            key={index}
                            className={cn(
                              'flex h-14 items-center justify-center rounded-xl border bg-background text-2xl transition-colors',
                              active &&
                                'group-has-[input:focus-visible]/code:border-foreground group-has-[input:focus-visible]/code:shadow-[inset_0_0_0_1px_var(--foreground)]',
                            )}
                          >
                            {field.value?.[index] ?? ''}
                          </span>
                        );
                      })}
                    </div>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        onChange={(event) => {
                          const code = event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
                          field.onChange(code || undefined);
                          if (code.length === CODE_LENGTH) void form.handleSubmit(submit)();
                        }}
                        inputMode="numeric"
                        className="absolute inset-0 h-14 w-full rounded-xl border-0 bg-transparent text-transparent caret-transparent selection:bg-transparent focus-visible:border-0 aria-invalid:border-0 aria-invalid:ring-0 dark:bg-transparent"
                        autoComplete="one-time-code"
                        autoFocus
                        data-test="auth-code-input"
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
        {state.error && (
          <p role="alert" className="text-destructive text-sm">
            {state.error}
          </p>
        )}
        <Button
          type="submit"
          disabled={state.busy}
          size="marketing"
          className="btn-primary w-full"
          data-test="auth-submit"
        >
          {state.busy && <Spinner />}
          {state.sent ? (state.busy ? 'Verifying…' : 'Continue') : 'Email me a code'}
        </Button>
        {!confirmation && (
          <p className="text-[13px] leading-relaxed text-muted-foreground">
            By continuing, you confirm you’re 18 or older and agree to our{' '}
            <Link className="underline" href="/terms-of-service">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link className="underline" href="/privacy-policy">
              Privacy Policy
            </Link>
            .
          </p>
        )}
        {state.sent && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            {!confirmation && (
              <Button
                type="button"
                variant="link"
                className="px-0"
                onClick={() => {
                  form.setValue('code', undefined);
                  setState((s) => ({ ...s, sent: false, error: '', resendIn: 0 }));
                }}
                data-test="auth-change-email"
              >
                Use a different email
              </Button>
            )}
            <Button
              type="button"
              variant="link"
              className="px-0"
              disabled={state.busy || state.resendIn > 0}
              onClick={() => void resend()}
              data-test="auth-resend-code"
            >
              {state.resendIn > 0 ? `Send a new code in ${state.resendIn}s` : 'Send a new code'}
            </Button>
          </div>
        )}
      </form>
    </Form>
  );
}
